import React from 'react';
import {
  ShieldCheck, Compass, Target, CalendarCheck, TrendingUp,
  MessageSquare, Bell, UserCircle, ClipboardList, Sparkles,
  Handshake, ListChecks, Star, PenLine,
} from 'lucide-react';

export type GuideRole = 'mentor' | 'mentee';

export interface GuideStep { icon: React.ReactNode; title: string; text: string; tip?: string; }
export interface GuideFeature { icon: React.ReactNode; title: string; text: string; }
export interface GuideJourney { icon: React.ReactNode; label: string; }

export interface GuideContent {
  role: GuideRole;
  badge: string;
  titleLead: string;
  titleAccent: string;
  lead: string;
  journey: GuideJourney[];
  steps: GuideStep[];
  features: GuideFeature[];
}

const journeyBase: GuideJourney[] = [
  { icon: <ShieldCheck size={18} />, label: 'Acuerdo' },
  { icon: <Compass size={18} />, label: 'FODA' },
  { icon: <Target size={18} />, label: 'PDI' },
  { icon: <CalendarCheck size={18} />, label: 'Sesiones' },
  { icon: <TrendingUp size={18} />, label: 'Avance' },
];

export const GUIDE_CONTENT: Record<GuideRole, GuideContent> = {
  mentee: {
    role: 'mentee',
    badge: 'Guía del mentee',
    titleLead: 'Tu programa de mentoría,',
    titleAccent: 'paso a paso',
    lead: 'Esta es tu ruta durante el programa: desde firmar el acuerdo hasta construir tu Plan de Desarrollo Individual y trabajarlo sesión a sesión con tu mentor. Aquí te explicamos cómo aprovechar cada parte.',
    journey: journeyBase,
    steps: [
      {
        icon: <ShieldCheck size={22} />,
        title: 'Firma tu acuerdo',
        text: 'Al ingresar, revisa y acepta el acuerdo del programa: propósito, confidencialidad y compromisos. Es lo que activa tu relación con tu mentor.',
        tip: 'Todo lo que conversen es confidencial. Ese es el pacto que hace posible la mentoría.',
      },
      {
        icon: <Compass size={22} />,
        title: 'Completa tu FODA',
        text: 'Identifica tus Fortalezas, Oportunidades, Debilidades y Amenazas. Sé concreto: una capacidad nombrada con un caso que la respalde vale más que una generalidad.',
        tip: 'Las debilidades y amenazas que priorices se convierten en los objetivos de tu PDI.',
      },
      {
        icon: <Target size={22} />,
        title: 'Construye tu PDI',
        text: 'Define tu Norte (dónde quieres estar al final del programa) y un objetivo por cada brecha prioritaria. A cada objetivo le sumas acciones bajo el modelo 70·20·10.',
        tip: 'Puedes guardarlo como borrador e ir afinándolo junto a tu mentor.',
      },
      {
        icon: <CalendarCheck size={22} />,
        title: 'Agenda tus sesiones',
        text: 'Coordina con tu mentor y agenda tu sesión del mes. Elige la fecha, la modalidad y el objetivo que van a trabajar.',
        tip: 'El programa contempla una sesión por mes durante los 12 meses.',
      },
      {
        icon: <PenLine size={22} />,
        title: 'Registra y califica cada sesión',
        text: 'Después de reunirte, marca las acciones que avanzaste, califica la sesión y deja tus notas. Así tu PDI refleja tu progreso real.',
        tip: 'Tu calificación es confidencial y ayuda a cuidar la calidad del programa.',
      },
    ],
    features: [
      { icon: <Target size={20} />, title: 'Tu plan', text: 'FODA, PDI y sesiones en un solo lugar, siempre a mano.' },
      { icon: <MessageSquare size={20} />, title: 'Mensajería', text: 'Conversa con tu mentor sin salir de la plataforma.' },
      { icon: <Bell size={20} />, title: 'Notificaciones', text: 'Recordatorios de lo que sigue: firmar, agendar, registrar.' },
      { icon: <UserCircle size={20} />, title: 'Tu perfil', text: 'Actualiza tus datos y cambia tu contraseña cuando quieras.' },
    ],
  },

  mentor: {
    role: 'mentor',
    badge: 'Guía del mentor',
    titleLead: 'Acompaña a tu mentee,',
    titleAccent: 'con método',
    lead: 'Tu rol es acelerar el desarrollo de tu mentee. Esta guía te muestra cómo seguir su FODA y su PDI, sostener sesiones con foco y dejar feedback que sume, mes a mes.',
    journey: journeyBase,
    steps: [
      {
        icon: <ShieldCheck size={22} />,
        title: 'Acepta el acuerdo',
        text: 'Revisa y firma el acuerdo del programa. Define el marco de confidencialidad y compromiso con tu mentee desde el día uno.',
        tip: 'La confianza es la base: nada de lo conversado se comparte fuera de la dupla.',
      },
      {
        icon: <Handshake size={22} />,
        title: 'Conoce a tu mentee',
        text: 'Cuando tu mentee entregue su FODA y su PDI, revísalos con calma. Ahí están sus brechas prioritarias, su Norte y las acciones que se propone.',
        tip: 'Entra a "Mis mentees" para ver su ficha, su plan y su avance.',
      },
      {
        icon: <ListChecks size={22} />,
        title: 'Comenta y orienta el PDI',
        text: 'Deja comentarios en cada objetivo: valida el foco, sugiere acciones y ayuda a equilibrar el modelo 70·20·10. Tu mirada afina el plan.',
        tip: 'Un buen objetivo es específico y accionable, no un deseo general.',
      },
      {
        icon: <CalendarCheck size={22} />,
        title: 'Sostén las sesiones',
        text: 'Agenda y gestiona las sesiones mensuales. Entra con un foco claro (revisión de FODA, construcción del PDI o avances) y cierra con compromisos.',
        tip: 'Puedes publicar tu disponibilidad para que la coordinación sea más simple.',
      },
      {
        icon: <Star size={22} />,
        title: 'Registra y da feedback',
        text: 'Al terminar cada sesión, registra lo conversado, califica y deja tu feedback. Es lo que convierte una buena conversación en desarrollo sostenido.',
        tip: 'El feedback que se repite suele ser el más valioso: nómbralo con ejemplos.',
      },
    ],
    features: [
      { icon: <ClipboardList size={20} />, title: 'Mis mentees', text: 'Ficha, FODA, PDI y avance de cada persona que acompañas.' },
      { icon: <MessageSquare size={20} />, title: 'Mensajería', text: 'Coordina y acompaña entre sesiones, todo registrado.' },
      { icon: <Bell size={20} />, title: 'Notificaciones', text: 'Te avisa cuándo agendar, registrar o dar seguimiento.' },
      { icon: <Sparkles size={20} />, title: 'Recursos', text: 'Materiales de apoyo para enriquecer tus sesiones.' },
    ],
  },
};
